import classnames from 'classnames/bind';
import style from './questionItem.module.scss';
import React from 'react';
import { QuestionType } from '@domain/constant/questionType';
import { JobTypeCode } from '@domain/constant/v1/jobType';
import { useInterview, InterviewQuestionType } from '@components/pass/interview/start/context/interviewContext';
import {
  useDeleteCustomQuestion,
  useCustomQuestionListKeys,
} from '@repository/interviewPractices/pooledQuestions/usePooledQuestions';
import { useJobGroupList } from '@repository/codes/useCodes';
import { JobGroupDto } from '@domain/rs/profile/career/JobGroupRs';
import { queryClient } from '@utils/queryClient';
import { CompanyQuestionVO } from '@domain/vo/pass/interview/question/CompanyQuestionVO';

const cx = classnames.bind(style);

interface QuestionItemProps {
  data: InterviewQuestionType;
}

const InterviewPracticeQuestionItem = ({ data }: QuestionItemProps) => {
  const { sn: questionId, type: questionType } = data;
  const { selectedQuestionList, updateSelectedQuestionList, activeTab, policies } = useInterview();
  const { data: jobGroupData } = useJobGroupList();
  const jobGroupList = jobGroupData?.jobTitleGroups;
  const { mutateAsync: deleteCustomQuestion } = useDeleteCustomQuestion(() => refetchCustomQuestionList());

  const isSelectedTab = activeTab === QuestionType.SELECTED;
  const isMySelfTab = activeTab === QuestionType.MYSELF;
  const isDeletable = isSelectedTab || isMySelfTab;
  const isClickable = isSelectedTab ? false : selectedQuestionList.length < policies.interviewPracticeMaxQuestionSize;
  const isChecked = isSelectedTab ? false : selectedQuestionList?.some((v) => v.sn === questionId);

  const refetchCustomQuestionList = () => {
    queryClient.invalidateQueries(useCustomQuestionListKeys);
  };

  const handleQuestionClick = () => {
    if (isClickable || isChecked) updateSelectedQuestionList(data);
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!isDeletable) return;
    if (isSelectedTab) updateSelectedQuestionList(data);
    if (isMySelfTab) deleteCustomQuestion(questionId);
  };

  const sectionName = () => {
    switch (questionType) {
      case QuestionType.COMPANY:
        return (data as CompanyQuestionVO).companyName;
      case QuestionType.FREQUENCY:
        return '빈출 면접 질문';
      case QuestionType.MYSELF:
        return '내가 만든 질문';
      default:
        return '';
    }
  };

  const sectionNameClass = () => {
    switch (questionType) {
      case QuestionType.COMPANY:
        return 'sectionNameCompany';
      case QuestionType.FREQUENCY:
        return 'sectionNameFrequency';
      case QuestionType.MYSELF:
        return 'sectionNameMyself';
      default:
        return '';
    }
  };

  const jobType = () => {
    if (!(data instanceof CompanyQuestionVO)) return '';

    switch (data.jobTypeCode) {
      case JobTypeCode.NEW:
        return '신입';
      case JobTypeCode.CAREER:
        return '경력';
      case JobTypeCode.INTERNSHIP:
        return '인턴';
      default:
        return '';
    }
  };

  const jobGroup = () => {
    if (!(data instanceof CompanyQuestionVO)) return '';

    if (jobGroupList) {
      if (data.jobGroupCode === -1) {
        return '직군 공통';
      } else {
        const idx = jobGroupList.findIndex((it: JobGroupDto) => it.code === data.jobGroupCode);
        if (idx !== -1) {
          return jobGroupList[idx].name;
        }
      }
    }

    return '';
  };

  const jobTypeResult = jobType();
  const jobGroupResult = jobGroup();

  return (
    <li
      className={cx(['questionItem', sectionNameClass(), { checked: isChecked }, { clickable: isClickable }])}
      onClick={handleQuestionClick}
    >
      <div className={cx('sectionTag')}>
        <div className={cx('tag', 'sectionName', sectionNameClass())}>{sectionName()}</div>
        {jobTypeResult && <div className={cx('tag')}>{jobType()}</div>}
        {jobGroupResult && <div className={cx('tag')}>{jobGroup()}</div>}
      </div>
      <div className={cx('description')}>
        <span className={cx('intro')}>Q.</span>
        <span className={cx('question')}>{data.questionText}</span>
      </div>
      <button className={cx('btnDelete', { isDeletable })} onClick={handleDeleteClick} />
    </li>
  );
};

export default InterviewPracticeQuestionItem;
